import type { SalesPointConfigurationResponseDTO } from '@/types/dto/agent';

// `isDefault` is missing from the generated schema on this branch, which
// predates its addition to the API contract — the sales point does return it,
// and the search widget's own form schema keys off it. Declared optional here
// rather than regenerating the whole schema on a release branch; callers must
// treat an absent value as "not the default type", which is what the widget
// does too.
export type PosPassengerType = NonNullable<
  SalesPointConfigurationResponseDTO['passengerTypes']
>[number] & { isDefault?: boolean };

// Derives a plausible age for a passenger from their POS passenger type's
// validity criteria. The search widget's schema requires an age for every
// non-default passenger type (age-based search tenants), but a deep link's
// `passengers` parameter names a `passengerTypeId` and no age (BR-58550).
// Any age inside the type's [ageFrom, ageTo] window resolves back to the same
// passenger type, so a synthetic in-range value keeps the search valid
// without changing which fares apply.
//
// The midpoint is clamped to ageFrom + 10 so open-ended ranges (e.g. adult
// 18–999) yield a sane value (28) instead of the literal midpoint (508).
export function deriveAgeFromPassengerType(
  posType: PosPassengerType
): number | undefined {
  const criteria = posType.validityCriterias?.[0];
  if (
    typeof criteria?.ageFrom !== 'number' ||
    typeof criteria?.ageTo !== 'number'
  ) {
    return undefined;
  }
  return Math.min(
    Math.floor((criteria.ageFrom + criteria.ageTo) / 2),
    criteria.ageFrom + 10
  );
}
